import React from 'react';
import { cn } from '@/lib/utils';
import { NAVY, withAlpha } from './mobileTheme';

/**
 * MobileActionButton — the universal compact action button (spec 1.2).
 * Softly-squared (rounded-lg, not a pill), >=44px touch target, icon+label,
 * labels never truncate. Primary uses the module accent; secondary/neutral are
 * lightly tinted. Preserves module branding via the `accent` prop.
 *
 * props: { icon?, variant: 'primary'|'secondary'|'neutral', accent, fullWidth, ... }
 */
export const MobileActionButton = React.forwardRef(
  ({ icon: Icon, children, variant = 'primary', accent = NAVY, fullWidth = false, className, ...props }, ref) => {
    const base =
      'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold leading-tight ' +
      'transition-[filter,background-color,border-color] duration-150 focus-visible:outline-none focus-visible:ring-2 ' +
      'disabled:opacity-50 disabled:pointer-events-none';
    const byVariant = {
      primary: 'text-white shadow-sm hover:brightness-110',
      secondary: 'border border-slate-200 bg-slate-100 text-[#0E2A47] hover:bg-slate-200',
      neutral: 'border border-slate-200 bg-white text-[#0E2A47] hover:bg-slate-50',
    };
    const style = {
      ['--tw-ring-color']: withAlpha(accent, 0.5),
      ...(variant === 'primary' ? { backgroundColor: accent } : {}),
    };
    return (
      <button ref={ref} className={cn(base, byVariant[variant], fullWidth && 'w-full', className)} style={style} {...props}>
        {Icon && <Icon size={16} className="shrink-0" />}
        <span className="whitespace-normal">{children}</span>
      </button>
    );
  },
);
MobileActionButton.displayName = 'MobileActionButton';

/**
 * MobileActionBar — responsive container for 1–N action buttons (spec 1.3).
 * On mobile each button grows to fill a row; two compact buttons sit side by side
 * and longer ones wrap to the next line. On sm+ buttons take their natural width.
 */
export const MobileActionBar = ({ children, className, testid = 'mobile-action-bar' }) => (
  <div
    data-testid={testid}
    className={cn('flex flex-wrap gap-2 [&>*]:min-w-[140px] [&>*]:flex-1 sm:[&>*]:min-w-0 sm:[&>*]:flex-none', className)}
  >
    {children}
  </div>
);

export default MobileActionButton;
